import React, { useState, useEffect, useMemo } from 'react';
import { WarningGreyIcon } from 'assests/GmsIcons';
import Table from 'Components/DesignSystem/Table/Table';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';

type TCommonAttributesProps = {
    recategorizationResponse: RecategorizationResponse | undefined
    payload: TRecategorizePayload
    setPayload: React.Dispatch<React.SetStateAction<TRecategorizePayload>>
}

/**
 * Step 2, first tab — the attributes both categories share, each one keepable or not.
 *
 * On DS `Table` since 2026-09-13. Every derivation below is verbatim: `getDataFromPayload`
 * still defaults `isChecked` to TRUE when the payload has no entry yet, `updatePayload`
 * still writes the WHOLE list back (the backend derives keep-vs-drop from each row's own
 * flag — see Review.tsx), and the resync effect still re-derives on either input changing.
 *
 * Two things the DS now owns that this file used to hand-roll:
 *   - the select-all's INDETERMINATE state, which was a `useRef` writing
 *     `headerCheckboxRef.current.indeterminate` in an effect. `Checkbox` takes it as a
 *     prop and writes the DOM property itself, so the ref and its effect are gone.
 *   - expansion, which was a local `Set<string>` plus a hand-rolled chevron. It is still
 *     caller-owned state (`Table` holds none) — only the chevron and the detail `<tr>`
 *     moved.
 */
const CommonAttributeTable = ({ recategorizationResponse, payload, setPayload }: TCommonAttributesProps) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set([]))

    const getDataFromPayload = (): TAttributeRow[] => {
        if (!recategorizationResponse?.common_attr_list || !payload.common_attr_list) {
            return []
        }

        return recategorizationResponse.common_attr_list.map((attr) => {
            const payloadAttr = payload.common_attr_list.find(
                (p) => p.attribute_id === attr.attribute_id
            )

            return {
                id: attr.attribute_id,
                sourceCategory: attr.attribute_name,
                targetCategory: attr.attribute_name,
                isChecked: payloadAttr?.checked ?? true,
                isExpandable: true,
                commonValues: attr.attribute_value?.map((val) => val.value) || [],
            }
        })
    }

    const [tableData, setTableData] = useState<TAttributeRow[]>(getDataFromPayload())

    useEffect(() => {
        setTableData(getDataFromPayload())
    }, [payload, recategorizationResponse])

    const checkedRows = new Set(
        tableData.filter((row) => row.isChecked).map((row) => row.id)
    )

    const isAllChecked = checkedRows.size === tableData.length && tableData.length > 0
    const isIndeterminate = checkedRows.size > 0 && checkedRows.size < tableData.length

    const toggleExpand = (id: string) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(id)) {
                newSet.delete(id)
            } else {
                newSet.add(id)
            }
            return newSet
        })
    }

    const updatePayload = (updatedRows: TAttributeRow[]) => {
        const updatedAttrList = updatedRows.map((row) => ({
            attribute_id: row.id,
            attribute_name: row.sourceCategory,
            checked: row.isChecked,
        }))

        setPayload((prev) => ({
            ...prev,
            common_attr_list: updatedAttrList,
        }))
    }

    const toggleCheck = (id: string) => {
        const updatedData = tableData.map((row) =>
            row.id === id ? { ...row, isChecked: !row.isChecked } : row
        )
        setTableData(updatedData)
        updatePayload(updatedData)
    }

    const toggleAll = () => {
        const newCheckedState = !isAllChecked
        const updatedData = tableData.map((row) => ({
            ...row,
            isChecked: newCheckedState,
        }))
        setTableData(updatedData)
        updatePayload(updatedData)
    }

    const columns = useMemo<TableColumn<TAttributeRow>[]>(() => [
        {
            id: 'selected',
            header: '',
            accessor: 'isChecked',
            width: '8%',
            cellType: 'custom',
            /*
             * The select-all lives in the header CELL. `TableColumn.header` is typed
             * `string`, so the control goes through `headerInfo` — the slot that exists
             * precisely so a header can carry a control without being wrapped in the
             * sort button.
             */
            headerInfo: (
                <Checkbox
                    checked={isAllChecked}
                    indeterminate={isIndeterminate}
                    onChange={toggleAll}
                    data-testid="selectattributes-commonattributetable-input-1"
                    aria-label="Select all common attributes"
                />
            ),
            render: (_v, row) => (
                <Checkbox
                    checked={row.isChecked}
                    onChange={() => toggleCheck(row.id)}
                    data-testid={`recategorize-common-row-${row.id}`}
                    aria-label={row.sourceCategory}
                />
            ),
        },
        {
            id: 'source',
            header: payload?.source_category_name ?? '',
            accessor: 'sourceCategory',
            width: '46%',
        },
        {
            id: 'target',
            header: payload?.target_category_name ?? '',
            accessor: 'targetCategory',
            width: '46%',
        },
    ], [isAllChecked, isIndeterminate, tableData, payload?.source_category_name, payload?.target_category_name]);

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-h-auto tw-overflow-y-auto">
            <Table<TAttributeRow>
                columns={columns}
                data={tableData}
                rowKey="id"
                striped
                bordered
                data-testid="recategorize-common-attributes-table"
                emptyContent={null}
                expandedRowKeys={[...expandedRows]}
                onExpandChange={(key) => toggleExpand(String(key))}
                expandLabel={(row, expanded) => `${expanded ? 'Collapse' : 'Expand'} ${row.sourceCategory}`}
                renderExpandedRow={(row) => (
                    <div className="tw-px-6 tw-py-2">
                        {row.commonValues?.length ? (
                            <div>
                                <Typography variant="caption-metadata" as="p" className="tw-text-ink-tertiary tw-font-semibold">
                                    Common Attribute Values
                                </Typography>
                                <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mt-2">
                                    {row.commonValues.map((value) => (
                                        <span
                                            key={value}
                                            className="tw-px-2 tw-py-1 tw-bg-surface-sunken tw-rounded-sm tw-text-xs tw-text-ink"
                                        >
                                            {value}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="tw-text-ink-tertiary">
                                No attribute values available
                            </div>
                        )}
                    </div>
                )}
                footer={checkedRows.size < tableData.length ? (
                    <div className="tw-px-6 tw-py-4 tw-flex tw-items-center tw-gap-2 tw-text-ink-tertiary">
                        <WarningGreyIcon />
                        <span>Attribute values for this attribute will be blank</span>
                    </div>
                ) : undefined}
            />
        </div>
    )
}

export default CommonAttributeTable
